/**
 * EPN-98 — the single, pure definition of "is this build allowed to talk to mock gateways?".
 *
 * Deliberately has NO `import.meta.env` access so the exact same rule can be unit-tested, reused by
 * `vite.config.ts` (build time) and by `productionMockGuard.ts` (browser start-up) without drift.
 *
 * Why strict: `env.ts` and `features/identity/services/index.ts` both compute
 * `(VITE_USE_MOCKS ?? 'true').toLowerCase() !== 'false'` — i.e. an ABSENT, empty, misspelled or
 * whitespace-padded value silently selects the in-memory Mock gateways (which fabricate payroll, filing
 * and payment data). That convenient default is kept for local development and automated tests, but a
 * production bundle must never inherit it. The only value a production bundle may carry is exactly
 * `false` — lowercase, no trimming, no aliases. `FALSE`, `' false'`, `0`, `no`, `''`, `true`, `1`, `yes` and
 * anything malformed are all rejected rather than "fixed up": an unsafe Production value must be an obvious
 * deployment error, never silently coerced.
 */
export function isExplicitRealBackendSelection(rawUseMocks: string | undefined | null): boolean {
  return rawUseMocks === 'false';
}

/**
 * `VITE_USE_BUSINESS_PROFILE_MOCKS` (see `env.ts`) is a second, per-feature mock switch that OVERRIDES
 * `VITE_USE_MOCKS` for the Business Profile Setup gateway only (it exists for the Playwright suite). A
 * production bundle may therefore carry it only as absent (the gateway then follows `VITE_USE_MOCKS`) or
 * exactly `false`.
 */
export function isSafeBusinessProfileMockOverride(raw: string | undefined | null): boolean {
  return raw === undefined || raw === null || raw === 'false';
}

/** Returns a human-readable reason when the mock switches are NOT an explicit real-backend selection, else null. */
export function mockModeViolation(
  rawUseMocks: string | undefined | null,
  rawBusinessProfileMocks?: string | undefined | null,
): string | null {
  if (!isSafeBusinessProfileMockOverride(rawBusinessProfileMocks)) {
    return (
      `Refusing to produce or run a Production bundle: VITE_USE_BUSINESS_PROFILE_MOCKS must be unset or exactly ` +
      `"false", but it is set to ${JSON.stringify(rawBusinessProfileMocks)}. It overrides VITE_USE_MOCKS for the ` +
      `Business Profile Setup gateway and would select an in-memory mock in Production. ` +
      `See docs/production-mock-safety.md.`
    );
  }

  if (isExplicitRealBackendSelection(rawUseMocks)) {
    return null;
  }

  const found = rawUseMocks === undefined || rawUseMocks === null ? 'not set' : `set to ${JSON.stringify(rawUseMocks)}`;
  return (
    `Refusing to produce or run a Production bundle: VITE_USE_MOCKS must be exactly "false" (real backend), ` +
    `but it is ${found}. Mock gateways fabricate payroll, tax-filing and payment data and must never ship ` +
    `to Production. See docs/production-mock-safety.md.`
  );
}

/** Throws when `isProductionBuild` and the build is not an explicit real-backend selection. No-op otherwise. */
export function assertRealBackendForProduction(
  isProductionBuild: boolean,
  rawUseMocks: string | undefined | null,
  rawBusinessProfileMocks?: string | undefined | null,
): void {
  if (!isProductionBuild) {
    return;
  }

  const violation = mockModeViolation(rawUseMocks, rawBusinessProfileMocks);
  if (violation !== null) {
    throw new Error(violation);
  }
}
